import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { WithTransformProps } from '@bingads-webui-react/hoc-with-transform-props';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getCurrentScope, Scope } from '@bingads-webui-react/with-site-map';
import { isAccountStatusValidToSwitch, hasEditPermissions } from '@bingads-webui-react/smart-switch-to-expert';

import { SwitchToExpertMenu } from './switch-to-expert-menu';

export const withSwitchToExpert = (WrappedComponent) => {
  const HoC = (props) => {
    const permissions = useContext(PermissionsContext);
    const {
      scope: {
        values: {
          aid,
          campaignId,
          adGroupId,
        },
      },
    } = useContext(ScopeContext);

    const isAccountScope = getCurrentScope(campaignId, adGroupId, aid) === Scope.Account;
    // Only show this when in account scope
    const isSmartMode = isAccountScope && permissions.IsSmartMode;
    const hasPermission =
      hasEditPermissions(permissions) &&
      isAccountStatusValidToSwitch(props.account);

    const SwitchToExpertWithProps = useMemo(() =>
      WithTransformProps(SwitchToExpertMenu, componentProps => ({
        ...componentProps,
        account: props.account,
        serverContext: props.serverContext,
        settingsContext: props.settingsContext,
        onChangeToExpertMode: props.onChangeToExpertMode,
        onCancelExpertMode: props.onCancelExpertMode,
        shouldReload: props.shouldSwitchToExpertReload,
      })), [
      props.account,
      props.onCancelExpertMode,
      props.onChangeToExpertMode,
      props.serverContext,
      props.settingsContext,
      props.shouldSwitchToExpertReload,
    ]);

    const TopMenuComponent = (isSmartMode && hasPermission) ?
      SwitchToExpertWithProps : props.TopMenuComponent;

    return (<WrappedComponent
      {...props}
      TopMenuComponent={TopMenuComponent}
    />);
  };

  HoC.displayName = `withSwitchToExpert(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    serverContext: PropTypes.shape({}).isRequired,
    settingsContext: PropTypes.shape({}).isRequired,
    account: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      Name: PropTypes.string.isRequired,
      AccountNumber: PropTypes.string.isRequired,
      ParentCustomerId: PropTypes.number.isRequired,
      AccountMode: PropTypes.number.isRequired,
      InactiveReasons: PropTypes.number.isRequired,
    }).isRequired,
    onChangeToExpertMode: PropTypes.func,
    onCancelExpertMode: PropTypes.func,
    shouldSwitchToExpertReload: PropTypes.bool,
    TopMenuComponent: PropTypes.elementType,
  };

  HoC.defaultProps = {
    onChangeToExpertMode: undefined,
    onCancelExpertMode: undefined,
    shouldSwitchToExpertReload: undefined,
    TopMenuComponent: undefined,
  };

  return hoistNonReactStatics(HoC, WrappedComponent);
};
